import type { Container } from 'pixi.js';
import type { MetaPopupController } from '../../metaAbstractions/types';
import type { IObservableValue } from '../../mainMenu/types';
import type {
  CollectionEventAppState,
  CollectionEventConfig,
  CollectionEventInfo,
  CollectionEventSystemConfig,
} from './types';
import type { ResourceMap } from '../../app/resources/types';
import type { TileColor } from '../../game/boardMVC/TileColor';
import { CollectionEventProgressBarView } from './views/CollectionEventProgressBarView';

import {
  addTempTokensToCollectionEvent,
  clearCollectionEventTempTokens,
  getCurrentEventInstance,
  getTempTokensAmount,
  getTokensToCollect,
  transferTempTokensInCollectionEvent,
} from './model/collectionEventUtils';
import { startCollectionEventMenuFlow } from './model/startCollectionEventMenuFlow';
import { CollectionTokenIconView } from './views/CollectionTokenIconView';
import { openCollectionEventPopup } from './model/openCollectionEventPopup';
import type { GameEventController } from '../types';
import { LoseProgressBaseIconView } from '../../game/views/popups/failFlow/icons/LoseProgressBaseIconView';
import type { BoardModel } from '../../game/boardMVC/models/board/BoardModel';
import type { BoardView } from '../../game/boardMVC/views/board/BoardView';
import { BoardEventType, TileRemovalReason } from '../../game/boardMVC/models/board/BoardEvents';
import { getCollectionEventDebugFunctions } from './getCollectionEventDebugFunctions';
import { CollectionEventInGameTrayView } from './views/CollectionEventInGameTrayView';

export interface CollectionEventDependencies {
  config: CollectionEventSystemConfig;
  popupsController: MetaPopupController;
  observable: IObservableValue<{
    levelIndex: number;
    collectionEvent: CollectionEventAppState;
    language: string | null;
  }>;
  addResource: (eventInfo: CollectionEventInfo, resources: ResourceMap) => void;
  onEvent?: {
    onTokensAdded?: (config: CollectionEventConfig, tokens: number) => void;
    onEventFinished?: (config: CollectionEventConfig, progress: number) => void;
  };
}

export class CollectionEventController implements GameEventController {
  public readonly featureName = 'Collection Event';

  private progressBarViewInstance: CollectionEventProgressBarView | null = null;

  private inGameTrayViewInstance: CollectionEventInGameTrayView | null = null;

  private lockProgressBarPopupOpen = false;

  constructor(private readonly dependencies: CollectionEventDependencies) {}

  public async startMainMenuFlow(): Promise<void> {
    this.lockProgressBarPopupOpen = true;
    await startCollectionEventMenuFlow(this.dependencies, this.progressBarViewInstance);
    this.lockProgressBarPopupOpen = false;
  }

  public setupInGameHandler(boardModel: BoardModel, boardView: BoardView): void {
    boardModel.events.addListener(BoardEventType.TilesRemoved, async (event) => {
      const tokensToCollect = this.getTokensToCollect();

      if (event.reason !== TileRemovalReason.Match) return;
      if (!tokensToCollect) return;
      if (event.tiles.length === 0) return;
      if (!tokensToCollect.includes(event.tiles[0].color)) return;

      this.addTempTokens(event.tiles.length);

      const tileStack = boardView.getStackView(event.stackId);
      if (!tileStack) return;
      if (!this.inGameTrayViewInstance) return;

      await boardView.wait(500);
      void this.inGameTrayViewInstance.animateAddCollectedTokens(event.tiles.length, tileStack.getGlobalPosition());
    });
  }

  public onLevelWon(): void {
    this.transferTempTokens();
  }

  public onLevelLost(): void {
    this.clearTempTokens();
  }

  private addTempTokens(tokensAmount: number): void {
    addTempTokensToCollectionEvent(this.dependencies, tokensAmount);
  }

  private transferTempTokens(): void {
    transferTempTokensInCollectionEvent(this.dependencies);
  }

  public getTempTokensAmount(): number | null {
    return getTempTokensAmount(this.dependencies);
  }

  public createLostProgressBadge(): Container | null {
    const event = getCurrentEventInstance(this.dependencies);
    if (!event) return null;
    if (event.tempTokens <= 0) return null;
    const collectionEventProgressIcon = new CollectionTokenIconView(event.visuals).showAmountToLose(event.tempTokens);

    return new LoseProgressBaseIconView().injectIcon(collectionEventProgressIcon).toggleAmountView(false);
  }

  public getTokensToCollect(): TileColor[] | null {
    return getTokensToCollect(this.dependencies);
  }

  public clearTempTokens(): void {
    clearCollectionEventTempTokens(this.dependencies);
  }

  public createProgressBarView(): CollectionEventProgressBarView {
    this.progressBarViewInstance?.destroy({ children: true });

    this.progressBarViewInstance = new CollectionEventProgressBarView(this.dependencies);

    this.progressBarViewInstance.onClicked.connect(() => {
      if (this.lockProgressBarPopupOpen) return;
      void openCollectionEventPopup(this.dependencies, false);
    });

    this.progressBarViewInstance.on('destroyed', () => {
      this.progressBarViewInstance = null;
    });

    return this.progressBarViewInstance;
  }

  public createInGameTrayView(): CollectionEventInGameTrayView | null {
    const event = getCurrentEventInstance(this.dependencies);
    if (!event) return null;

    this.inGameTrayViewInstance?.destroy({ children: true });
    
    this.inGameTrayViewInstance = new CollectionEventInGameTrayView({
      eventConfig: event,
    });

    this.inGameTrayViewInstance.on('destroyed', () => {
      this.inGameTrayViewInstance = null;
    });

    return this.inGameTrayViewInstance;
  }

  public createCollectionIcon(tokensAmount: number): Container | null {
    const event = getCurrentEventInstance(this.dependencies);
    if (!event) return null;

    const icon = new CollectionTokenIconView(event.visuals);
    void icon.animateCollect(tokensAmount);
    return icon;
  }

  public getDebugFunctions(): Record<string, () => void> {
    return getCollectionEventDebugFunctions(this.dependencies);
  }
}
